import { type ClipboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateNodeInternals } from 'reactflow';
import JsonView from 'react18-json-view';
import { Box } from '@mui/material';
import 'react18-json-view/src/style.css';
import 'react18-json-view/src/dark.css';
import '@/UI/JsonViewer/json-viewer.css';
import { I18N_KEYS } from '@/language/keys';
import { FlexCol, Flex } from '@/UI/styles';
import { color } from '@/colors';
import { AppToggleButtons } from '@/UI/AppToggleButtons/AppToggleButtons';
import { useGlobalStore } from '@/state/global.state';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { safeJsonParse } from '@/utils/strings.utils';
import { isIterator } from '@/utils/iterator.utils';
import { getResizableDimensions } from '@/consts/node-ui.consts';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { useIsHovered } from '@/hooks/useIsHovered';
import { usePromptView, useFlowView } from '../Recipe/FlowContext';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { hasEditingPermissions } from './Utils';
import { PROMPT_MAX_CHARS } from './consts/validations.consts';
import { PromptEditor } from './PromptNodeComponents/PromptEditor';
import { DisplayMode } from './PromptNodeComponents/constants';

import type { AvailableInput } from './PromptNodeComponents/types';
import type { NodeId } from 'web';
import type { PromptData } from '@/types/node';
import type { AddNewNodeOptions } from '../Recipe/flowTypes';

const PROMPT_NODE_RESIZABLE_DIMENSIONS = getResizableDimensions(120, 'small');

interface PromptNodeV3Props {
  id: NodeId;
  data: PromptData;
  addNewNode: (options: AddNewNodeOptions) => void;
}

export function PromptNodeV3({ id, data, addNewNode }: PromptNodeV3Props) {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const view = usePromptView(id);
  const flowView = useFlowView();
  const { prompt = '', inputNodes, displayMode = DisplayMode.SOURCE_VALUE } = data;
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const [hasFocus, setHasFocus] = useState(false);
  const [forceTextMode, setForceTextMode] = useState(false);
  const inputHandles = data.handles.input as Record<string, { label?: string }>;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const {
    isHovered: isScrollContainerHovered,
    onMouseEnter: onScrollMouseEnter,
    onMouseLeave: onScrollMouseLeave,
  } = useIsHovered();
  useCaptureElementWheelScroll(isScrollContainerHovered || hasFocus ? scrollContainerRef : { current: null });

  // Notify React Flow when input handles change
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    // Update React Flow's internal state when input handles are added/removed
    updateNodeInternals(id);
  }, [inputHandles, id, updateNodeInternals]);

  const truncatedWarningText = useMemo(() => {
    return t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.TRUNCATED_WARNING, { max: PROMPT_MAX_CHARS });
  }, [t]);

  const showTruncatedWarning = useCallback(() => {
    updateSnackbarData({
      text: truncatedWarningText,
      severity: 'error',
      icon: null,
      isOpen: true,
    });
  }, [truncatedWarningText, updateSnackbarData]);

  const handlePromptEditorChange = useCallback(
    (prompt: string) => {
      view.setPrompt(prompt, false);
    },
    [view],
  );

  const handlePaste = useCallback(
    (evt: ClipboardEvent<HTMLDivElement>) => {
      const pastedText = evt.clipboardData.getData('text');
      if (!pastedText) {
        return;
      }
      if (pastedText.length > PROMPT_MAX_CHARS) {
        showTruncatedWarning();
      }
    },
    [showTruncatedWarning],
  );

  const handleAddInputHandle = useCallback(() => {
    return view.addInputHandle();
  }, [view]);

  // Build availableInputs with key, label, value, and isIterator for each input handle
  const availableInputs: AvailableInput[] = useMemo(() => {
    const hasInputs = inputNodes && inputNodes.length > 0;
    if (!hasInputs) return [];

    return (
      inputNodes?.map(([key, value]) => {
        // Get the display label from the handle configuration
        const handleLabel = inputHandles[key]?.label ?? key;
        // Evaluate the input node to get its string value
        const sourceNodeId = value?.nodeId;
        let stringValue: string | undefined;

        const evaluatedValue = flowView.evaluateInput(id, key);
        if (evaluatedValue?.type === 'string') {
          stringValue = evaluatedValue.value;
        }

        // Check if the source node is an iterator
        const sourceNode = sourceNodeId ? flowView.getNodeNullable(sourceNodeId) : undefined;
        const isSourceIterator = sourceNode ? isIterator(sourceNode.data) : false;

        return { key, label: handleLabel, value: stringValue, isIterator: isSourceIterator };
      }) ?? []
    );
  }, [inputNodes, inputHandles, flowView, id]);

  const hasInputHandles = availableInputs.length > 0;
  const jsonViewerSrc = useMemo(() => safeJsonParse<Record<string, unknown> | unknown[]>(prompt, true), [prompt]);
  const isJsonFormat = jsonViewerSrc && !forceTextMode && !hasInputHandles;

  return (
    <DynamicNode2
      id={id}
      data={data}
      className="prompt"
      isResizable
      resizableDimensions={PROMPT_NODE_RESIZABLE_DIMENSIONS}
    >
      <FlexCol
        sx={{
          alignItems: 'flex-end',
          width: '100%',
          flex: 1,
          minHeight: 0,
          cursor: !hasEditingPermissions(role, data) ? 'default' : '',
          pointerEvents: !hasEditingPermissions(role, data) ? 'none' : 'auto',
          position: 'relative',
        }}
      >
        {jsonViewerSrc && !hasInputHandles && hasEditingPermissions(role, data) && (
          <Flex
            data-testid="prompt-node-v3-format-toggle"
            className="nodrag"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: color.Black92,
              p: 0.4,
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            <AppToggleButtons
              value={isJsonFormat ? 'json' : 'text'}
              options={[
                { value: 'text', label: 'Text' },
                { value: 'json', label: 'JSON' },
              ]}
              onChange={(value) => setForceTextMode(value === 'text')}
              sx={{
                '& .MuiButtonBase-root.MuiToggleButton-root.Mui-selected': {
                  background: color.White64_T,
                  color: color.Black92,
                },
                '& .MuiButtonBase-root.MuiToggleButton-root.Mui-selected:hover': {
                  background: color.White80_T,
                  color: color.Black92,
                },
                '& .MuiButtonBase-root.MuiToggleButton-root:hover': {
                  background: color.White16_T,
                },
              }}
            />
          </Flex>
        )}
        {isJsonFormat ? (
          <Box
            ref={scrollContainerRef}
            className="nodrag"
            onMouseEnter={onScrollMouseEnter}
            onMouseLeave={onScrollMouseLeave}
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              backgroundColor: color.Black84,
              border: `1px solid ${color.White08_T}`,
              borderRadius: 1,
              p: 1,
              pointerEvents: 'auto',
            }}
          >
            <JsonView
              src={jsonViewerSrc ?? {}}
              dark
              theme="vscode"
              enableClipboard={false}
              displaySize="collapsed"
              displayArrayIndex={false}
              collapseStringsAfterLength={50}
              collapseObjectsAfterLength={5}
              editable={hasEditingPermissions(role, data)}
              onEdit={({ src }) => {
                // Convert the updated object back to a JSON string
                const updatedJsonString = JSON.stringify(src, null, 2);
                view.setPrompt(updatedJsonString, false);
              }}
              onAdd={({ src }) => {
                const updatedJsonString = JSON.stringify(src, null, 2);
                view.setPrompt(updatedJsonString, false);
              }}
              onDelete={({ src }) => {
                const updatedJsonString = JSON.stringify(src, null, 2);
                // If deleting the whole JSON object, set the prompt to an empty string
                const value = updatedJsonString === prompt ? '' : updatedJsonString;
                view.setPrompt(value, false);
              }}
            />
          </Box>
        ) : (
          <PromptEditor
            nodeId={id}
            availableInputs={availableInputs}
            value={prompt}
            placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.PLACEHOLDER)}
            maxLength={PROMPT_MAX_CHARS}
            minHeight={data.height ? undefined : PROMPT_NODE_RESIZABLE_DIMENSIONS.defaultTextareaHeight}
            scrollContainerRef={scrollContainerRef}
            onMouseEnter={onScrollMouseEnter}
            onMouseLeave={onScrollMouseLeave}
            onChange={handlePromptEditorChange}
            onPaste={handlePaste}
            hasEditingPermissions={hasEditingPermissions(role, data)}
            displayMode={displayMode}
            addNewNode={addNewNode}
            addInputHandle={handleAddInputHandle}
            hasFocus={hasFocus}
            setHasFocus={setHasFocus}
          />
        )}
      </FlexCol>
    </DynamicNode2>
  );
}
